import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { AlertCircleIcon, CheckIcon, ImageIcon, Loader2Icon, MailIcon, PhoneIcon, XIcon } from 'lucide-react';
import { Button, LinkButton } from '../ui/Button';
import { TextArea, TextField } from '../ui/Field';
import { useAdminData } from '../../contexts/AdminDataContext';
import { MAX_IMAGE_BYTES, validateImage } from '../../services/imageService';
import { telHref } from '../../utils/format';
const ease = [0.23, 1, 0.32, 1] as const;
const emptyForm = {
  name: '',
  phone: '',
  email: '',
  address: '',
  description: ''
};
const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
interface Selected {
  file: File;
  preview: string;
}
export function ContactSection() {
  const {
    createEnquiry,
    settings
  } = useAdminData();
  const [form, setForm] = useState(emptyForm);
  const [selected, setSelected] = useState<Selected[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [fileError, setFileError] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  function validate(): boolean {
    const next: Record<string, string> = {};
    if (!form.name.trim()) next.name = 'Skriv dit navn.';
    if (form.phone.replace(/[^\d]/g, '').length < 8) next.phone = 'Skriv et gyldigt telefonnummer.';
    if (form.email && !emailPattern.test(form.email.trim())) next.email = 'Skriv en gyldig e-mailadresse.';
    if (!form.description.trim()) next.description = 'Beskriv kort opgaven.';
    setErrors(next);
    return Object.keys(next).length === 0;
  }
  function removeImage(index: number) {
    setSelected(current => {
      const target = current[index];
      if (target) URL.revokeObjectURL(target.preview);
      return current.filter((_, i) => i !== index);
    });
  }
  return <section id="kontakt" className="scroll-mt-24" data-id="element-98" data-mp="r0gdn51tyh8g">
      <div className="mx-auto max-w-content px-5 py-16 md:py-24 lg:px-8" data-id="element-99" data-mp="w2fikjd7xm7i">
        <div className="grid gap-10 lg:grid-cols-[0.85fr_1.15fr] lg:gap-16" data-id="element-100" data-mp="2s40rs2v43hd4">
          <div className="lg:sticky lg:top-28 lg:self-start" data-id="element-101" data-mp="2o2xe9n0jdxf2">
            <h2 className="font-display text-[clamp(2rem,5.5vw,3.5rem)] font-bold leading-[1.05] tracking-tight text-ink" data-id="element-102" data-mp="2o4i97dqrjidg">
              Fortæl om din opgave
            </h2>
            <p className="mt-5 max-w-md text-[17px] leading-relaxed text-ink-soft" data-id="element-103" data-mp="2x2kfo8t9n384">
              Beskriv kort hvad du skal have lavet. Du må meget gerne vedhæfte billeder, så er det nemmere at
              vurdere opgaven.
            </p>

            <div className="mt-8 flex flex-col gap-3 sm:flex-row lg:flex-col" data-id="element-104" data-mp="36vj3vpkuorr7">
              {settings.phone && <LinkButton href={telHref(settings.phone)} variant="secondary" size="lg" className="sm:w-auto lg:w-full" data-id="element-105" data-mp="3fu3hwdfgud93">
                  <PhoneIcon className="h-4 w-4" aria-hidden="true" data-id="element-106" data-mp="1lgc0grqwap33" />
                  {settings.phone}
                </LinkButton>}
              {settings.email && <LinkButton href={`mailto:${settings.email}`} variant="secondary" size="lg" className="sm:w-auto lg:w-full" data-id="element-107" data-mp="5x4n4lt7f9af">
                  <MailIcon className="h-4 w-4" aria-hidden="true" data-id="element-108" data-mp="3scgwynbotrjq" />
                  {settings.email}
                </LinkButton>}
            </div>
          </div>

          {sent ? <motion.div initial={{
          opacity: 0,
          scale: 0.97
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.28,
          ease
        }} role="status" className="flex flex-col items-start rounded-card border border-line bg-white p-8 md:p-12" data-id="element-109" data-mp="3jck56cu04ckg">
              <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-accent-soft text-accent-deep" data-id="element-110" data-mp="13lxznwgb9j5j">
                <CheckIcon className="h-6 w-6" aria-hidden="true" data-id="element-111" data-mp="17louqccvelfv" />
              </span>
              <h3 className="mt-6 font-display text-2xl font-bold tracking-tight text-ink md:text-3xl" data-id="element-112" data-mp="ivbydyuvefjp">
                Tak for din henvendelse.
              </h3>
              <p className="mt-3 text-[17px] text-ink-soft" data-id="element-113" data-mp="8wv2qokzlcrb">
                LYKKEVÆRK vender tilbage hurtigst muligt.
              </p>
              <Button variant="secondary" size="md" className="mt-8" onClick={() => setSent(false)} data-id="element-114" data-mp="3y1qt0lcltt9">
                Send en opgave mere
              </Button>
            </motion.div> : <form noValidate className="rounded-card border border-line bg-white p-6 md:p-9" onSubmit={async event => {
          event.preventDefault();
          if (submitting) return;
          if (!validate()) return;
          setSubmitting(true);
          setSubmitError('');
          const result = await createEnquiry({
            ...form,
            files: selected.map(item => item.file)
          });
          setSubmitting(false);
          if (!result.ok) {
            setSubmitError(result.error ?? 'Henvendelsen kunne ikke sendes. Prøv igen, eller ring til os i stedet.');
            return;
          }
          selected.forEach(item => URL.revokeObjectURL(item.preview));
          setSelected([]);
          setForm(emptyForm);
          setErrors({});
          setSent(true);
        }} data-id="element-115" data-mp="3racervkqzzrl">
              <p className="mb-5 text-sm text-ink-muted" data-id="element-116" data-mp="1e4fio444xsqd">
                Felter markeret med <span className="text-accent" data-id="element-117" data-mp="1ivnq4ulwn40w">*</span> skal udfyldes.
              </p>

              <div className="grid gap-5 sm:grid-cols-2" data-id="element-118" data-mp="1ixh0ajvq86dn">
                <TextField id="navn" label="Navn" required autoComplete="name" placeholder="Dit navn" value={form.name} error={errors.name} onChange={event => setForm({
              ...form,
              name: event.target.value
            })} data-id="element-119" data-mp="lfgxey4puagu" />
                <TextField id="telefon" label="Telefonnummer" required type="tel" inputMode="tel" autoComplete="tel" placeholder="12 34 56 78" value={form.phone} error={errors.phone} onChange={event => setForm({
              ...form,
              phone: event.target.value
            })} data-id="element-120" data-mp="s5026h4tn576" />
                <TextField id="email" label="E-mail" type="email" autoComplete="email" placeholder="dig@mail.dk" value={form.email} error={errors.email} onChange={event => setForm({
              ...form,
              email: event.target.value
            })} data-id="element-121" data-mp="9faai2wcuzjh" />
                <TextField id="omraade" label="Adresse eller område" autoComplete="street-address" placeholder="Fx Hvidovre" value={form.address} onChange={event => setForm({
              ...form,
              address: event.target.value
            })} data-id="element-122" data-mp="2xq4iq57ku537" />
              </div>

              <TextArea id="beskrivelse" label="Beskrivelse af opgaven" required className="mt-5" placeholder="Fortæl kort hvad der skal laves." value={form.description} error={errors.description} onChange={event => setForm({
            ...form,
            description: event.target.value
          })} data-id="element-123" data-mp="3hf58fvma2ivm" />

              <div className="mt-5" data-id="element-124" data-mp="483qp80rssch">
                <p className="mb-1.5 text-sm font-medium text-ink" data-id="element-125" data-mp="472549m4ongi">Upload billeder</p>
                <label htmlFor="billeder" className="flex cursor-pointer flex-col items-center justify-center gap-1.5 rounded-xl border border-dashed border-line bg-canvas px-6 py-7 text-center transition-colors duration-200 ease-smooth hover:border-accent/60" data-id="element-126" data-mp="916c2sve8vhl">
                  <ImageIcon className="h-5 w-5 text-ink-muted" strokeWidth={1.5} aria-hidden="true" data-id="element-127" data-mp="1vb3wkj847npf" />
                  <span className="text-[15px] font-medium text-ink" data-id="element-128" data-mp="1qc95yv3wouur">Vælg billeder</span>
                  <span className="text-sm text-ink-muted" data-id="element-129" data-mp="2nzrpn0z3vhdj">
                    Valgfrit — billeder op til {Math.round(MAX_IMAGE_BYTES / (1024 * 1024))} MB
                  </span>
                  <input id="billeder" type="file" multiple accept="image/*" className="sr-only" disabled={submitting} onChange={event => {
                const input = event.target;
                const files = Array.from(input.files ?? []);
                const problems: string[] = [];
                const accepted: Selected[] = [];
                files.forEach(file => {
                  const problem = validateImage(file);
                  if (problem) problems.push(problem);else accepted.push({
                    file,
                    preview: URL.createObjectURL(file)
                  });
                });
                setSelected(current => [...current, ...accepted]);
                setFileError(problems.join(' '));
                input.value = '';
              }} data-id="element-130" data-mp="2e23jzfzyopgl" />
                </label>

                {fileError && <p role="alert" className="mt-2 text-xs font-medium text-danger" data-id="element-131" data-mp="auw8hp2mqgzb">
                    {fileError}
                  </p>}

                {selected.length > 0 && <ul className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3" data-id="element-132" data-mp="2clzgs7p1rjgp">
                    {selected.map((item, index) => <li key={`${item.file.name}-${index}`} className="overflow-hidden rounded-xl border border-line" data-id="element-133" data-mp="2zfsrckhvmgou" data-mpi={index}>
                        <div className="relative" data-id="element-134" data-mp="39bnjc67zpo4a">
                          <img src={item.preview} alt={item.file.name} className="aspect-square w-full object-cover" data-id="element-135" data-mp="3ebry9rt92ase" />
                          <button type="button" onClick={() => removeImage(index)} aria-label={`Fjern billede ${item.file.name}`} className="absolute right-2 top-2 inline-flex h-8 w-8 items-center justify-center rounded-full bg-white/90 text-ink shadow-card transition-colors duration-200 ease-smooth hover:bg-white" data-id="element-136" data-mp="3nboh3csykffj">
                            <XIcon className="h-4 w-4" aria-hidden="true" data-id="element-137" data-mp="9v7aqfm4wt5u" />
                          </button>
                        </div>
                        <div className="flex items-center justify-between gap-2 px-3 py-2" data-id="element-138" data-mp="1qdr7egp4yp8t">
                          <span className="truncate text-xs text-ink-muted" data-id="element-139" data-mp="1leeat48gq3t1">{item.file.name}</span>
                          <button type="button" onClick={() => removeImage(index)} className="shrink-0 text-xs font-medium text-accent hover:text-accent-deep" data-id="element-140" data-mp="1ggcpecnkdsee">
                            Fjern
                          </button>
                        </div>
                      </li>)}
                  </ul>}
              </div>

              {Object.keys(errors).length > 0 && <p role="alert" className="mt-5 flex items-start gap-2 rounded-xl bg-accent-soft px-4 py-3 text-sm text-accent-deep" data-id="element-141" data-mp="2wuurt1zo0si0">
                  <AlertCircleIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" data-id="element-142" data-mp="q6vicgu4nvgj" />
                  Tjek de markerede felter, før du sender.
                </p>}

              {submitError && <p role="alert" className="mt-5 flex items-start gap-2 rounded-xl bg-danger/10 px-4 py-3 text-sm text-danger" data-id="element-143" data-mp="gfhnimd4fjtq">
                  <AlertCircleIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" data-id="element-144" data-mp="6gs2wltubl9s" />
                  {submitError}
                </p>}

              <Button type="submit" size="lg" className="mt-7 w-full text-lg" disabled={submitting} data-id="element-145" data-mp="3tmj5nb2jjcde">
                {submitting ? <>
                    <Loader2Icon className="h-5 w-5 animate-spin" aria-hidden="true" data-id="element-146" data-mp="2grpn4ih9g4b" />
                    {selected.length > 0 ? 'Sender og uploader billeder …' : 'Sender …'}
                  </> : 'Send forespørgsel'}
              </Button>
            </form>}
        </div>
      </div>
    </section>;
}